import { Form } from "@unform/web";
import { CalendarDays, ListChecks, Upload } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { Zoom, toast } from "react-toastify";
import api from "~/services/api";
import { getRegistries } from "~/functions";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { format, parseISO } from "date-fns";
import { AlertContext } from "~/App";
import { FullGridContext } from "../../..";
import FormData from "form-data";
import ImportForm from "./ImportForm";
import MonthPanel from "./MonthPanel";
import StatementView from "./StatementView";
import { monthLabel } from "../monthOptions";

export const InputContext = createContext({});

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

function importErrorMessage(payload) {
  if (!payload) return "Statement not imported!";

  if (payload.expected_start && payload.received_start) {
    return `Out of sequence: the next weekday to cover is ${safeDate(
      payload.expected_start
    )}, but the file starts on ${safeDate(payload.received_start)}.`;
  }

  if (payload.existing_period) {
    const { date_from, date_to } = payload.existing_period;
    return `Overlapping period: ${safeDate(date_from)} to ${safeDate(
      date_to
    )} was already imported.`;
  }

  return payload.error || "Statement not imported!";
}

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const { alertBox } = useContext(AlertContext);
  const { handleOpened, setSuccessfullyUpdated, successfullyUpdated } =
    useContext(FullGridContext);

  const [monthId, setMonthId] = useState(id);
  const [monthData, setMonthData] = useState({ loaded: false, statements: [] });
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("import");
  const [openStatementId, setOpenStatementId] = useState(null);
  const [importing, setImporting] = useState(false);

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function getMonthData(targetId = monthId) {
    if (!targetId || targetId === "new") {
      setMonthData((prev) => ({ ...prev, loaded: true }));
      return;
    }
    try {
      const { data } = await api.get(`/bankconciliation-months/${targetId}`);
      setMonthData((prev) => ({ ...prev, ...data, loaded: true }));

      const {
        created_by,
        created_at,
        updated_by,
        updated_at,
        canceled_by,
        canceled_at,
      } = data;
      setRegistry(
        await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        })
      );
    } catch (err) {
      toast(err?.response?.data?.error || "Could not load the month.", {
        type: "error",
        autoClose: 3000,
      });
      handleOpened(null);
    }
  }

  useEffect(() => {
    getMonthData();
    if (id !== "new") {
      setActiveMenu("month");
    }
  }, []);

  async function ensureMonth(data) {
    if (monthId && monthId !== "new") return monthId;

    try {
      const { data: created } = await api.post("/bankconciliation-months", {
        bankaccount_id: data.bankAccount.id,
        year: Number(data.year),
        month: Number(data.month),
      });
      return created?.id;
    } catch (err) {
      const existingId = err?.response?.data?.bank_conciliation_id;
      if (existingId) {
        return existingId;
      }
      throw err;
    }
  }

  async function handleImportSubmit(data) {
    if (!data?.bankAccount?.id) {
      toast("Select the bank account.", { type: "warning", autoClose: 2500 });
      return;
    }
    if (!data?.year || !data?.month) {
      toast("Select month and year.", { type: "warning", autoClose: 2500 });
      return;
    }
    if (!data?.import_file) {
      toast("Select the statement file.", { type: "warning", autoClose: 2500 });
      return;
    }

    setImporting(true);
    try {
      const resolvedMonthId = await ensureMonth(data);
      if (!resolvedMonthId) {
        toast("Could not resolve the reconciliation month.", {
          type: "error",
          autoClose: 3000,
        });
        return;
      }

      const formData = new FormData();
      formData.append("file", data.import_file);
      formData.append("bank_conciliation_id", resolvedMonthId);

      const { data: result } = await api.post(
        "/bankconciliations/import",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const period = result?.period;
      toast(
        `Statement imported: ${safeDate(period?.date_from)} to ${safeDate(
          period?.date_to
        )} — ${result?.total_items || 0} movements.`,
        { autoClose: 3000 }
      );

      if (result?.month_complete) {
        toast("The month is now fully covered.", {
          type: "success",
          autoClose: 3000,
        });
      }

      setSuccessfullyUpdated(true);
      setMonthId(resolvedMonthId);
      await getMonthData(resolvedMonthId);
      setActiveMenu("month");
    } catch (err) {
      toast(importErrorMessage(err?.response?.data), {
        type: "error",
        autoClose: 6000,
      });
    } finally {
      setImporting(false);
    }
  }

  function handleCloseMonth() {
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to close this month? This cannot be undone — the month cannot be reopened.",
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: "Yes, close it",
          onPress: async () => {
            try {
              await api.put(`/bankconciliation-months/${monthId}/close`);
              toast("Month closed!", { autoClose: 1500 });
              setSuccessfullyUpdated(true);
              await getMonthData();
            } catch (err) {
              toast(err?.response?.data?.error || "Could not close the month.", {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  const isNew = monthId === "new";
  const isClosed = monthData?.status === "Closed";

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      <div className="flex h-full flex-row items-start justify-between gap-4">
        <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
          {!isClosed ? (
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="import"
            >
              <Upload size={16} /> Import
            </RegisterFormMenu>
          ) : null}
          {!isNew ? (
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="month"
            >
              <CalendarDays size={16} /> Month
            </RegisterFormMenu>
          ) : null}
          {openStatementId ? (
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="statement"
            >
              <ListChecks size={16} /> Movements
            </RegisterFormMenu>
          ) : null}
        </div>

        <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
          <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
            <Form
              ref={generalForm}
              onSubmit={handleImportSubmit}
              className="w-full pb-32"
            >
              <InputContext.Provider
                value={{
                  id: monthId,
                  generalForm,
                  setSuccessfullyUpdated,
                  fullscreen,
                  setFullscreen,
                  successfullyUpdated,
                  handleCloseForm,
                }}
              >
                {isNew || monthData.loaded ? (
                  <>
                    <FormHeader
                      access={access}
                      title={
                        isNew
                          ? "New Bank Reconciliation"
                          : `Bank Reconciliation - ${monthLabel(
                              monthData?.month,
                              monthData?.year
                            )}`
                      }
                      registry={registry}
                      InputContext={InputContext}
                      loading={importing}
                      createText="Import"
                      saveText="Import statement"
                      disableSave={isClosed || activeMenu !== "import"}
                      className="relative overflow-hidden"
                    />

                    <div className={activeMenu === "import" ? "" : "hidden"}>
                      <ImportForm
                        InputContext={InputContext}
                        activeMenu
                        monthData={isNew ? null : monthData}
                        onAccountSelected={() => null}
                      />
                    </div>

                    {!isNew ? (
                      <div className={activeMenu === "month" ? "" : "hidden"}>
                        <InputLineGroup title="MONTH" activeMenu>
                          <MonthPanel
                            monthData={monthData}
                            onOpenStatement={(statement) => {
                              setOpenStatementId(statement.id);
                              setActiveMenu("statement");
                            }}
                            onCloseMonth={handleCloseMonth}
                          />
                        </InputLineGroup>
                      </div>
                    ) : null}

                    {openStatementId ? (
                      <div
                        className={activeMenu === "statement" ? "" : "hidden"}
                      >
                        <StatementView
                          statementId={openStatementId}
                          bankAccountId={monthData?.bankAccount?.id}
                          isClosed={isClosed}
                          onBack={() => setActiveMenu("month")}
                        />
                      </div>
                    ) : null}
                  </>
                ) : (
                  <FormLoading />
                )}
              </InputContext.Provider>
            </Form>
          </div>
        </div>
      </div>
    </Preview>
  );
}
